import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { ColumnDataTypes, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
/**
 * KIR-2917 — Hook Inventory contracts.
 *
 * Mirrors the AI Instructions inventory shape (see `./ai-instructions.ts`).
 * Both kinds of inventory items share the same `file_content` / `file_ownership`
 * storage; hooks are distinguished by `fileType='hook'`.
 */
// ============================================================================
// Source-family / platform / source-type vocabulary
// ============================================================================
/**
 * Top-level source family that drives the "Type + event" column copy.
 * - `coding-agent` covers Claude / Cursor / Copilot `settings.json` hooks.
 * - `git` covers `.git/hooks/`, `.husky/`, and `.pre-commit-config.yaml`
 *   entries; all show a single Git chip in the UI per spec.
 */
export const HOOK_SOURCE_FAMILIES = {
    CODING_AGENT: 'coding-agent',
    GIT: 'git',
};
// Derive the Zod enum from the canonical object so the two never drift.
export const hookSourceFamilySchema = z.enum(Object.values(HOOK_SOURCE_FAMILIES));
/**
 * Platform chip shown in the "IDE / Coding agent" column. Git-family hooks
 * collapse into a single `git` value regardless of whether they came from
 * `.git/hooks/`, `.husky/`, or `.pre-commit-config.yaml`.
 */
export const HOOK_PLATFORMS = {
    CLAUDE: 'claude',
    CURSOR: 'cursor',
    COPILOT: 'copilot',
    // GitHub Copilot CLI (the terminal `copilot` agent + VS Code Agent Mode) —
    // distinct from the `copilot` IDE-extension surface so ~/.copilot/hooks CLI
    // hooks don't conflate with IDE Copilot hooks in the Hooks tab (KIR-3179).
    GITHUB_COPILOT_CLI: 'github_copilot_cli',
    CODEX: 'codex',
    GIT: 'git',
};
export const hookPlatformSchema = z.enum(Object.values(HOOK_PLATFORMS));
/**
 * Granular reporter source-type. Persisted in `file_ownership.metadata` and
 * used to disambiguate git sub-sources in the drawer / filters.
 */
export const HOOK_SOURCE_TYPES = {
    CODING_AGENT: 'coding_agent',
    GIT_DIR: 'git_dir',
    HUSKY: 'husky',
    PRE_COMMIT_CONFIG: 'pre_commit_config',
};
export const hookSourceTypeSchema = z.enum(Object.values(HOOK_SOURCE_TYPES));
/**
 * Display labels for the granular source type (KIR-4432).
 *
 * Added because the Hook detail drawer rendered the raw persisted value —
 * `{hook.hookSourceType ?? '—'}` — so a user saw `pre_commit_config` while
 * `/api/v1` published `pre-commit-config` for the same row. Two spellings of one
 * value, neither of them a name. The drawer and the public contract's
 * `sourceTypeName` now both read from here.
 */
export const HOOK_SOURCE_TYPE_LABELS = Object.freeze({
    [HOOK_SOURCE_TYPES.CODING_AGENT]: 'Coding agent',
    [HOOK_SOURCE_TYPES.GIT_DIR]: 'Git hooks directory',
    [HOOK_SOURCE_TYPES.HUSKY]: 'Husky',
    [HOOK_SOURCE_TYPES.PRE_COMMIT_CONFIG]: 'pre-commit config',
});
/** The display label for a hook source type, or null when there is none to show. */
export function getHookSourceTypeLabel(sourceType) {
    if (sourceType == null) {
        return null;
    }
    return HOOK_SOURCE_TYPE_LABELS[sourceType] ?? sourceType;
}
/**
 * Compose the `fileCategory` value persisted on `file_ownership` rows. The
 * grouped inventory deduplicates by `(content_id, fileCategory, idePlatform)`
 * so two hooks with identical content but different events stay as separate
 * rows (per spec).
 */
export function hookFileCategory(family, event) {
    return `${family}:${event}`;
}
/**
 * Inverse of `hookFileCategory` — only used in UI/test paths that need to
 * split the column copy back into its parts. Returns `null` if the value
 * does not parse (older data, hand-crafted rows).
 */
export function parseHookFileCategory(fileCategory) {
    if (!fileCategory)
        return null;
    const idx = fileCategory.indexOf(':');
    if (idx <= 0 || idx === fileCategory.length - 1)
        return null;
    const familyRaw = fileCategory.slice(0, idx);
    const event = fileCategory.slice(idx + 1);
    const family = hookSourceFamilySchema.safeParse(familyRaw);
    if (!family.success)
        return null;
    return { family: family.data, event };
}
// ============================================================================
// Ingestion payload (POST /api/{account,organizations/:id}/hooks)
// ============================================================================
const sha256Schema = z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'must be a lowercase hex SHA-256');
/**
 * One discovered hook in an ingestion batch.
 */
export const hookReportItemSchema = z.object({
    /** Absolute path on the user's machine. */
    filePath: z.string().min(1),
    /** Raw hook script / configuration content. */
    content: z.string(),
    /** Pre-computed SHA-256 of `content` (lowercase hex). */
    contentHash: sha256Schema.optional(),
    /** Where the discoverer found this hook. */
    hookSourceType: hookSourceTypeSchema,
    /** Event name, e.g. `PreToolUse`, `pre-commit`, `commit-msg`. Bounded so a
     *  pathological reporter can't store an oversized `<family>:<event>`
     *  category that then participates in GROUP BY / indexing. */
    event: z.string().min(1).max(200),
    /** Platform chip target (claude / cursor / copilot / github_copilot_cli / codex / git). */
    platform: hookPlatformSchema,
    /** Workspace folder this hook applies to (project root). May be null for
     *  user-global hooks like `~/.claude/settings.json`. */
    workspaceFolder: z.string().nullable().optional(),
    /** Git reporting for project resolution (KIR-3356, forward-only). Lets a hook
     *  be matched to the same project as the repo's other files: gitOriginUrl is
     *  the normalized `origin` remote, repoRoot the repo top-level path. Both
     *  null/absent for user-global hooks not in a git repo. */
    gitOriginUrl: z.string().nullable().optional(),
    repoRoot: z.string().nullable().optional(),
});
/**
 * Full ingestion payload.
 */
export const hookReportSchema = z.object({
    hooks: z.array(hookReportItemSchema).max(500),
});
/**
 * Ingestion response — minimal acknowledgement.
 */
export const hookReportResponseSchema = z.object({
    accepted: z.number().int().min(0),
});
// ============================================================================
// Inventory listing
// ============================================================================
/**
 * Basic user info embedded in inventory rows (matches AI Instructions shape).
 */
export const hookUserBasicSchema = z.object({
    email: z.string().nullable(),
    name: z.string().nullable(),
});
/**
 * One grouped row in the Hook Inventory DataTable. Grouped by
 * `(content_id, fileCategory, idePlatform)` — so identical content under
 * different events surfaces as multiple rows, as the spec requires.
 */
export const hookInventoryRowSchema = z.object({
    contentId: z.string().uuid(),
    /** Aggregate identity for the row: <contentId>:<fileCategory>:<idePlatform>.
     *  URL-safe for `/hooks/:groupKey/{projects,users}` count modal endpoints. */
    groupKey: z.string(),
    name: z.string().nullable(),
    summary: z.string().nullable(),
    filePath: z.string(),
    filePaths: z.array(z.string()),
    /** `<family>:<event>` — what the Type+event column renders. */
    fileCategory: z.string().nullable(),
    /** Convenience split-out fields derived from fileCategory. */
    sourceFamily: hookSourceFamilySchema.nullable(),
    event: z.string().nullable(),
    /** Platform chip identity. */
    idePlatform: z.string().nullable(),
    /** Granular reporter source-type, e.g. `husky` vs `git_dir`. */
    hookSourceType: hookSourceTypeSchema.nullable(),
    users: z.array(hookUserBasicSchema),
    projects: z.array(z.string()),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
/**
 * One row inside the per-hook "users" count modal.
 */
export const hookUserSchema = z.object({
    userId: z.string().uuid(),
    userName: z.string().nullable(),
    userEmail: z.string().nullable(),
    /** Project ENTITY names the user has this hook in (worktrees collapsed into
     * their project), matching the entity-based Projects column/modal (KIR-3643). */
    projects: z.array(z.string()),
    lastSeenAt: z.string(),
});
export const listHookUsersResponseSchema = z.object({
    users: z.array(hookUserSchema),
});
/**
 * One row inside the per-hook "projects" count modal.
 */
export const hookProjectSchema = z.object({
    projectId: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    userCount: z.number(),
    lastSeenAt: z.string(),
});
export const listHookProjectsResponseSchema = z.object({
    projects: z.array(hookProjectSchema),
});
// ============================================================================
// Hook content (drawer)
// ============================================================================
export const hookContentResponseSchema = z.object({
    content: z.string(),
    generatedName: z.string().nullable(),
    generatedSummary: z.string().nullable(),
    contentHash: z.string(),
});
// ============================================================================
// DataTable schemas (filters / sort / response)
// ============================================================================
// `users` is intentionally absent: it is sortable (see the sort enum) but
// not filterable — the repository has no users HAVING-filter and the Hooks
// tab surfaces no Users column. Declaring it filterable would silently
// no-op any users filter in the query builder.
export const HOOK_FILTERABLE_FIELDS = {
    NAME: 'name',
    // `fileCategory` is the full `<family>:<event>` token shown in the
    // "Type + event" column — the surfaced filter, so a selection matches the
    // exact type+event. `family` is the coarser `<family>` segment (matches any
    // event of a family); it stays available for API consumers but has no column.
    FILE_CATEGORY: 'fileCategory',
    FAMILY: 'family',
    PLATFORM: 'platform',
    SOURCE_TYPE: 'sourceType',
    FILE: 'file',
    SUMMARY: 'summary',
    PROJECTS: 'projects',
};
export const HOOK_COLUMN_DATA_TYPES = {
    name: ColumnDataTypes.STRING,
    fileCategory: ColumnDataTypes.ENUM,
    family: ColumnDataTypes.ENUM,
    platform: ColumnDataTypes.ENUM,
    sourceType: ColumnDataTypes.ENUM,
    file: ColumnDataTypes.STRING,
    summary: ColumnDataTypes.TEXT,
    projects: ColumnDataTypes.ENUM,
};
// Fields backed by a server-side distinct-values endpoint (dynamic dropdowns).
// family/platform are kept dynamic — new coding-agent platforms add new hook
// families, and we don't want to hand-maintain a static option list. `summary`
// is a free-text TEXT filter (no value list); `name`/`sourceType` have no
// column in the Hooks tab so they're not surfaced.
export const HOOK_FILTER_OPTION_FIELDS = {
    FILE_CATEGORY: 'fileCategory',
    FAMILY: 'family',
    PLATFORM: 'platform',
    FILE: 'file',
    PROJECTS: 'projects',
};
export const hookFilterOptionFieldSchema = z.enum([
    'fileCategory',
    'family',
    'platform',
    'file',
    'projects',
]);
export const hookFilterOptionsQuerySchema = z.object({
    field: hookFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * Project filter option with metadata (KIR-3356, for the Projects dropdown).
 * `value` is the project slug (stable filter key); `label`/`name` the display
 * name.
 */
export const hookProjectFilterOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    name: z.string().nullable(),
});
export const hookFilterOptionsResponseSchema = z.object({
    field: hookFilterOptionFieldSchema,
    options: z.array(z.string()),
    // Present only for the `projects` field — entity options (slug + name).
    optionsWithMetadata: z.array(hookProjectFilterOptionSchema).optional(),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
export const hookDataTableSortFieldSchema = z.enum([
    'name',
    'family',
    'platform',
    'file',
    'summary',
    'projects',
    'users',
    'lastSeenAt',
]);
const hookDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: hookDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
});
export const hookDataTableQuerySchema = createTableQuerySchema(hookDataTableQueryBaseSchema, HOOK_COLUMN_DATA_TYPES);
export const hookTableResponseSchema = z.object({
    data: z.array(hookInventoryRowSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
/**
 * API client params for the hooks DataTable endpoint.
 */
export const listHooksDataTableParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: hookDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    filters: z.array(FiltersSchema.element).optional(),
});
//# sourceMappingURL=hooks.js.map